import { Component, inject, OnInit, OnDestroy } from '@angular/core';

import { Router, RouterOutlet } from '@angular/router';
import { SideMenuComponent, SideMenuItem } from '../side-menu/side-menu.component';
import { UserRole } from '../../../core/api';
import { AuthService } from '../../../services/auth.service';
import { UserService } from '../../../services/user.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SettingsService } from '../../../features/settings/services/settings.service';
import { Subscription } from 'rxjs';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { LoaderComponent } from '../loader/loader.component';
import { LoadingOverlayService } from '../../../services/loading-overlay.service';

@Component({
  selector: 'app-shell',
  imports: [RouterOutlet, SideMenuComponent, CdkScrollable, LoaderComponent, TranslocoDirective],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.css',
  standalone: true
})
export class AppShellComponent implements OnInit, OnDestroy {
  private readonly _subscription = new Subscription();
  private readonly _router = inject(Router);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _settingsService = inject(SettingsService);
  public readonly authService = inject(AuthService);
  public readonly loadingOverlay = inject(LoadingOverlayService);
  private readonly userService = inject(UserService);

  public collapsed = false;
  public userName = '';
  public userEmail = '';
  public companyName = '-';
  public avatarSrc: string = '';

  public menuFeatureItems: SideMenuItem[] = [
    {
      id: 'dashboard',
      icon: 'home',
      label: this._translocoService.translate('menuSidebar.featureItems.dashboard'),
      route: '/main/dashboard/overview'
    },
    {
      id: 'clients',
      icon: 'client',
      label: this._translocoService.translate('menuSidebar.featureItems.clients'),
      route: '/main/clients'
    },
    {
      id: 'calendar',
      icon: 'clock',
      label: this._translocoService.translate('menuSidebar.featureItems.tax-due-date-management'),
      route: '/main/tax-due-date-management'
    },
  ];

  public menuUserItems: SideMenuItem[] = [
    {
      id: 'settings',
      icon: 'settings',
      label: this._translocoService.translate('menuSidebar.userItems.userSettings'),
      route: '/main/settings'
    },
    { id: 'logout', icon: 'logout', label: this._translocoService.translate('menuSidebar.userItems.logout') }
  ];

  async ngOnInit(): Promise<void> {
    await this.userService.loadSubscriptionData();
    if (this.userService.hasRole([UserRole.Owner, UserRole.Admin]) && this.userService.isTemplateEditorAllowed()) {
      this.menuFeatureItems.push({
        id: 'notifications',
        icon: 'email',
        label: this._translocoService.translate('menuSidebar.featureItems.notifications'),
        route: '/main/notifications'
      });
    }

    // Initialize settings service to load tax office data
    await this._settingsService.initializeIfNeeded();

    // Get user info from auth service if available
    this._subscription.add(this.userService.fullUserDisplayName$.subscribe((fullUserDisplayName) => (this.userName = fullUserDisplayName)));
    this._subscription.add(this.userService.companyName$.subscribe((companyName) => (this.companyName = companyName)));
    // Subscribe to tax office data changes for company name and avatar
    this._subscription.add(
      this._settingsService.taxOfficeData$.subscribe((taxOfficeData) => {
        if (taxOfficeData) {
          this.companyName = taxOfficeData.name || 'TaxTimer';
          // Only use API logo URL if logoUrl exists in the data
          this.avatarSrc = taxOfficeData.logoUrl ? this._settingsService.getLogoApiUrl(taxOfficeData.id) || '' : '';
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public onMenuItemClicked(itemId: string): void {
    if (itemId === 'logout') {
      this.authService.logout();
      this._router.navigate(['/auth/login']);
      return;
    }
  }

  public onToggleCollapsed(collapsed: boolean): void {
    this.collapsed = collapsed;
  }
}
